import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "./common/Card";
import { Input } from "./common/Input";
import { Label } from "./common/Label";
import Button from "./common/Button";
import { ArrowLeft, Send, Image as ImageIcon, X, Mail } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./CustomConsentEditor.css";
import config from "../config";
import Spinner from "./common/Spinner";
import customToast from "./common/Toaster";
import { formatDateTimeDMY } from "../lib/ScheduleUtils";
import useBrandTranslation from "../hooks/useBrandTranslation";
import { apiJsonHeaders, withApiLocale } from "../utils/apiLocale";

export default function AssociationMessaging() {
  const { t } = useBrandTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [subscribersCount, setSubscribersCount] = useState(0);
  const quillRef = useRef(null);

  useEffect(() => {
    if (!user || user.role !== "association") {
      navigate("/");
      return;
    }
    loadMessages();
    const loadSubscribersCount = async () => {
      try {
        const response = await fetch(
          `${config.baseUrlApi}/associations/${user.id}/subscribers`,
          { headers: apiJsonHeaders() }
        );
        if (!response.ok) return;
        const result = await response.json();
        setSubscribersCount((result.data || []).length);
      } catch (err) {
        console.error("Error loading subscribers count:", err);
      }
    };
    loadSubscribersCount();
  }, [user, navigate]);

  const loadMessages = async () => {
    try {
      setLoadingMessages(true);
      const response = await fetch(
        `${config.baseUrlApi}/associations/${user.id}/messages`,
        { headers: apiJsonHeaders() }
      );
      if (!response.ok) throw new Error(t("app.messaging.loadMessagesError", "Error al cargar mensajes"));
      const result = await response.json();
      setMessages(result.data || []);
    } catch (err) {
      console.error("Error loading messages:", err);
      setError("Error al cargar los mensajes enviados");
    } finally {
      setLoadingMessages(false);
    }
  };

  // Handler para subir imágenes
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        customToast(t("app.messaging.imageTooLarge", "La imagen es demasiado grande. Máximo 5MB."), "danger");
        return;
      }

      try {
        setLoading(true);
        const formData = new FormData();
        formData.append("image", file);

        const response = await fetch(`${config.baseUrlApi}/utils/upload`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || "Error al subir la imagen");
        }

        const result = await response.json();
        const imageUrl = result.url;

        // Obtener la instancia de Quill
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          const index = range ? range.index : quill.getLength();
          // Insertar la imagen en el editor
          quill.insertEmbed(index, "image", imageUrl, "user");
          // Mover el cursor después de la imagen
          quill.setSelection(index + 1);
        }

        // También agregar a la lista de imágenes para guardar por separado
        setImages((prev) => [...prev, imageUrl]);
      } catch (error) {
        console.error("Error uploading image:", error);
        customToast(t("app.messaging.uploadError", "Error al subir la imagen: {{message}}", { message: error.message }), "danger");
      } finally {
        setLoading(false);
      }
    };
  }, []);

  // Configuración del editor Quill
  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [imageHandler]
  );

  const quillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "indent",
      "align",
      "link",
      "image",
    ],
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!subject.trim()) {
      setError("El asunto es obligatorio");
      return;
    }

    if (!message.trim() || message === "<p><br></p>") {
      setError("El mensaje es obligatorio");
      return;
    }

    try {
      setLoading(true);

      // Extraer URLs de imágenes del contenido HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(message, "text/html");
      const imgElements = doc.querySelectorAll("img");
      const imageUrls = Array.from(imgElements).map((img) => img.src);

      // Combinar con las imágenes adicionales
      const allImages = [...new Set([...imageUrls, ...images])];

      const response = await fetch(
        `${config.baseUrlApi}/associations/${user.id}/messages`,
        {
          method: "POST",
          headers: apiJsonHeaders(),
          body: JSON.stringify(
            withApiLocale({
              subject: subject.trim(),
              message: message.trim(),
              images: allImages,
            })
          ),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Error al enviar el mensaje");
      }

      setSuccess("Mensaje enviado exitosamente a todos los profesionales suscritos");
      setSubject("");
      setMessage("");
      setImages([]);
      
      // Limpiar el editor
      const quill = quillRef.current?.getEditor();
      if (quill) {
        quill.setText("");
      }

      // Recargar mensajes
      await loadMessages();
    } catch (err) {
      console.error("Error sending message:", err);
      setError(err.message || "Error al enviar el mensaje");
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== "association") {
    return null;
  }

  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-2">
      <div className="mb-4">
        <Button
          variant="outline"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          Mensajería de Asociación
        </h1>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Enviar Novedades a Suscriptores
            </span>
            <div className="text-sm  text-center font-normal text-gray-500 bg-gray-100 px-3 py-2 rounded-full">
              {subscribersCount} suscriptor{subscribersCount !== 1 ? "es" : ""}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="subject">{t("app.messaging.subject", "Asunto *")}</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("app.messaging.subjectPlaceholder", "Asunto del mensaje")}
                required
                disabled={loading}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="message">{t("app.messaging.message", "Mensaje *")}</Label>
              <div className="mt-1">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={message}
                  onChange={setMessage}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder={t("app.messaging.messagePlaceholder", "Escribe tu mensaje aquí...")}
                />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Puedes usar el editor de texto enriquecido para formatear tu mensaje y
                agregar imágenes.
              </p>
            </div>

            {error && (
              <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                {success}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSubject("");
                  setMessage("");
                  setImages([]);
                  setError("");
                  setSuccess("");
                  const quill = quillRef.current?.getEditor();
                  if (quill) {
                    quill.setText("");
                  }
                }}
                disabled={loading}
              >
                Limpiar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner className="w-4 h-4 mr-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Mensaje
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Historial de mensajes */}
      <Card className="mb-[100px]">
        <CardHeader>
          <CardTitle>{t("app.messaging.sentMessages", "Mensajes Enviados")}</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingMessages ? (
            <div className="flex justify-center py-8">
              <Spinner className="w-8 h-8" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No has enviado ningún mensaje aún.
            </p>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-lg">{msg.subject}</h3>
                      <p className="text-sm text-gray-500">
                        {formatDateTimeDMY(msg.created_at)}
                      </p>
                    </div>
                  </div>
                  <div
                    className="text-gray-700 mt-2"
                    dangerouslySetInnerHTML={{ __html: msg.message }}
                  />
                  {msg.images && msg.images.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                      {msg.images.map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`Imagen ${idx + 1}`}
                          className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-90"
                          onClick={() => window.open(img, "_blank")}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

